import { useState, type ReactNode } from "react";
import { useSearchParams } from "react-router-dom";
import { FaGithub } from "react-icons/fa";
import { FcGoogle } from "react-icons/fc";
import { Button } from "@/components/ui/button";
import { authClient } from "@/lib/auth-client";

type Mode = "signin" | "signup";

interface OAuthButtonsProps {
  mode: Mode;
}

interface Provider {
  // Better-Auth socialProviders key — needs a matching server entry.
  slug: "google" | "github";
  name: string;
  icon: ReactNode;
}

const PROVIDERS: Provider[] = [
  {
    slug: "google",
    name: "Google",
    // Multi-colour Google "G" — brand-correct on any background.
    icon: <FcGoogle size={20} />,
  },
  {
    slug: "github",
    name: "GitHub",
    // Monochrome, uses currentColor → follows button text colour, so it
    // stays visible on dark AND light themes.
    icon: <FaGithub size={18} aria-hidden />,
  },
];

export function OAuthButtons({ mode }: OAuthButtonsProps) {
  const [params] = useSearchParams();
  const next = params.get("next") || "/console";
  const [pending, setPending] = useState<Provider["slug"] | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function start(provider: Provider) {
    setPending(provider.slug);
    setError(null);
    try {
      // Better-Auth's social signin: server returns the upstream
      // authorize URL; the client navigates the browser there. On
      // return Google redirects to /api/auth/callback/<provider>,
      // Better-Auth establishes the session (sets cookie + responds
      // with `set-auth-token`), then redirects to `callbackURL` —
      // the SPA boot then exchanges the cookie for a bearer token
      // via its first /api/auth/get-session call.
      const callbackURL = new URL(next, window.location.origin).toString();
      const result = await authClient.signIn.social({
        provider: provider.slug,
        callbackURL,
      });
      if (result.error) {
        setError(result.error.message ?? `Could not start ${provider.name} sign-in`);
        setPending(null);
      }
    } catch (err) {
      setError((err as Error).message ?? `Could not start ${provider.name} sign-in`);
      setPending(null);
    }
  }

  return (
    <div className="grid grid-cols-1 gap-2">
      {PROVIDERS.map((p) => (
        <Button
          key={p.slug}
          variant="outline"
          size="lg"
          type="button"
          disabled={pending !== null}
          className="h-11 w-full justify-center gap-3 text-base"
          onClick={() => start(p)}
        >
          {p.icon}
          {pending === p.slug
            ? `Redirecting to ${p.name}…`
            : `${mode === "signin" ? "Sign in with " : "Continue with "}${p.name}`}
        </Button>
      ))}
      {error && (
        <p className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
          {error}
        </p>
      )}
    </div>
  );
}
